import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, User, Loader2, Mail } from "lucide-react";

const ROLES = ["Partner", "Associato", "Praticante", "Paralegal"];
const STATUSES = ["In Studio", "In Tribunale", "Smart Working", "In Ferie"];
const SEDI = ["Studio Napoli", "Studio Roma", "Studio Milano"];

const statusStyle = (s) => ({
  "In Studio": "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  "In Tribunale": "bg-red-500/15 text-red-700 border-red-500/30",
  "Smart Working": "bg-blue-500/15 text-blue-700 border-blue-500/30",
  "In Ferie": "bg-stone-500/15 text-stone-600 border-stone-500/30",
}[s] || "bg-stone-200 text-stone-600");

export default function CollaboratorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { name: "", role: "Associato", email: "", sede: "Studio Milano", status: "In Studio" };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/collaborators"); setItems(r.data); }
    catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) { toast.error("Inserisci il nome"); return; }
    setSaving(true);
    try {
      const res = await api.post("/collaborators", form);
      setItems((i) => [...i, res.data]);
      toast.success("Collaboratore aggiunto");
      setOpen(false); setForm(empty);
    } catch (e) { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try { const res = await api.patch(`/collaborators/${id}`, { status }); setItems((i) => i.map((x) => (x.id === id ? res.data : x))); }
    catch (e) { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/collaborators/${id}`); setItems((i) => i.filter((x) => x.id !== id)); toast.success("Rimosso"); }
    catch (e) { toast.error("Errore"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-stone-500">{items.length} collaboratori nello Studio</p>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-collaborator-btn" className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Collaboratore
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} className="card-luxe rounded-xl p-5" data-testid={`collaborator-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#F4EFE4] border border-amber-500/30 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-stone-900 font-medium leading-tight">{c.name}</div>
                    <div className="text-xs text-amber-600/80">{c.role}</div>
                  </div>
                </div>
                <button onClick={() => remove(c.id)} data-testid={`delete-collaborator-${c.id}`} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="text-xs text-stone-500 space-y-1 mb-3">
                {c.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {c.email}</div>}
                {c.sede && <div>{c.sede}</div>}
              </div>
              <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                <SelectTrigger data-testid={`collaborator-status-${c.id}`} className={`h-9 text-xs border ${statusStyle(c.status)}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border border-amber-500/20 text-stone-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nuovo Collaboratore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Nome *</Label>
              <Input value={form.name} data-testid="collaborator-name-input" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900" placeholder="Avv. Nome Cognome" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Sede</Label>
                <Select value={form.sede} onValueChange={(v) => setForm((f) => ({ ...f, sede: v }))}>
                  <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{SEDI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-[#F4EFE4] border-stone-300 text-stone-900" /></div>
            <div className="space-y-1.5"><Label className="text-stone-600 text-sm">Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="bg-[#F4EFE4] border-stone-300 text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-stone-300 text-stone-900">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button onClick={save} disabled={saving} data-testid="save-collaborator-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
